import { SectionTitle } from "@/components/base/ui/SectionTitle";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import React from "react";
import { Head } from "@/components/base/Head";
import { Breadcrumb } from "@/components/base/Breadcrumb";
import Link from "next/link";
import Image from "next/image";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { PriceList } from "@/components/base/ui/PriceList";
import { ScrollToTop } from "@/components/base/ui/ScrollToTop";

const PlansPage = () => {
  const META_TITLE = "プランの確認";
  const breadcrumbs = [
    {
      title: META_TITLE,
    },
  ];

  const faqs = [
    {
      question: "中小企業の戦略ラボとは？",
      answer:
        "中小企業の戦略ラボは、忙しい社長さんでも「自社の会社力」を底上げできる、経営のオンラインプラットフォームです。<br /><br />中小企業・小規模事業者の皆さまがついつい後回しにしがちな経営課題の解決に向け、PDCA（計画・実行・検証・改善）を着実に回すことができる日本初のサービスです。",
    },
    {
      question: "契約途中のプランの変更は可能ですか？",
      answer: "可能です。",
    },
    {
      question: "解約方法について教えて下さい",
      answer: `・解約方法の詳細につきましてはこちらをご覧ください<br><a class="underline" href="https://tayori.com/faq/95dd3fab44b53f9e7089a48211fbca96638ff9ef/detail/596a36414d6b757e1527157b82fe22327e9a349e/" class="text-sm" rel="noreferrer" target="_blank">https://tayori.com/faq/95dd3fab44b53f9e7089a48211fbca96638ff9ef/detail/596a36414d6b757e1527157b82fe22327e9a349e/</a>`,
    },
  ];

  const CvLink = () => {
    return (
      <Link
        href={"/registrations/new"}
        className={
          "text-white bg-[#E9530C] text-xl md:text-2xl flex items-center justify-center py-4 max-w-[320px] m-auto hover:opacity-60 duration-300 rounded font-bold"
        }
      >
        会員登録はこちら
        <ArrowRightIcon className={"w-5 ml-2"} />
      </Link>
    );
  };

  return (
    <>
      <Head title={META_TITLE} canonicalPath={`/plans`} />
      <main className={"pb-20"}>
        <div className={"mb-10 max-w-[1440px] m-auto px-4 py-2"}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>
        <div className={"max-w-[1100px] m-auto px-5"}>
          <div className={"mb-20"}>
            <div className={"mb-8"}>
              <div className={`flex justify-between`}>
                <h1 className={"text-2xl md:text-3xl"}>
                  <LockClosedIcon
                    className={
                      "inline-block mr-1 -mt-1 w-6 md:w-8 text-orange-400"
                    }
                  />
                  <span className={"leading-[1.75]"}>
                    忙しい会社さんでも「学習・実践・定着」が簡単に出来る！
                  </span>
                </h1>
              </div>
            </div>
            <div className={"md:flex items-center"}>
              <div
                className={
                  "rounded-lg leading-[0] overflow-hidden mb-4 md:mb-0"
                }
              >
                <Image
                  src={"/images/premium.png"}
                  width={480}
                  height={252}
                  alt={""}
                />
              </div>
              <div className={"md:pl-5 flex-1"}>
                <h2 className={"flex items-center text-2xl font-bold mb-2"}>
                  自社の会社力=生産性を
                  <br />
                  高めるための機能が満載！
                </h2>
                <p>
                  マンガなどで楽しく学べる学習コンテンツ、業務での実践に向けた支援ツール、学びの定着を実現する評価シートやAI機能など、忙しい中でも着実に自社の会社力を高めることができます。
                </p>
              </div>
            </div>
          </div>
          <div className={"mb-20"}>
            <div className={"mb-8"}>
              <SectionTitle title={"会員比較表"} />
            </div>
            <PriceList />
            <div className={"mb-10"}>
              <CvLink />
            </div>
            <div className={"text-sm opacity-80 break-all"}>
              <p className={"mb-3 leading-[1.75]"}>
                ※お申込み・ご利用にあたってのご注意（請求書決済の場合は利用月の月初請求、月末期限のお支払いとなります）
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・有料プランにご登録の場合、14日間は一切料金が発生しません。お試し期間が終わるとお客様ご自身で解約手続きを行わない限り、お申込み時にお選びいただいたプランとお支払い方法で自動的にご利用が継続され、ご利用料金が発生いたします。
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・ご利用カード情報はいつでも変更できます。
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・何かしらの理由によりご登録いただいたクレジットカードの利用承認が得られなかった場合、お申し込みをキャンセル扱いとさせて頂きます。
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・契約とご利用開始ステップについて
                <br />
                手続きは4つの流れからなります。
                <br />
                ①メールアドレスの入力とパスワードの設定
                <br />
                ②メールアドレス宛に届いた認証リンクの確認
                <br />
                ③プランの選択とクレジットカード情報の登録（無料会員の場合は不要）
                <br />
                ④企業情報の入力です。支払い方法につきましては現在クレジットカード払いのみになります。企業情報の入力を終えるとすぐにご利用可能となります。
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・導入設定について
                <br />
                弊社にて導入設定を承っております。詳細につきましてはこちらをご覧ください。
                <br />
                <a
                  className={"underline"}
                  href={
                    "https://s-d-lab.jp/wp-content/uploads/2023/07/introduction_detail.pdf"
                  }
                  rel="noreferrer"
                  target={"_blank"}
                >
                  https://s-d-lab.jp/wp-content/uploads/2023/07/introduction_detail.pdf
                </a>
              </p>
              <p className={"mb-3 leading-[1.75]"}>
                ・お支払いと解約・返金について
                <br />
                初期費用はかかりません。有料プランの場合、サービスの利用開始から14日間を過ぎて解約がない場合、本契約として月額費用をいただきます。その後は開始日から1ヶ月ごとにお支払いいただきます。解約を行うとプランはキャンセルされますが、当月分までのお支払いをお願いしています。解約忘れによる返金対応は致しかねますのであらかじめご了承ください。
              </p>
              <p className={"leading-[1.75]"}>
                ・解約方法の詳細につきましてはこちらをご覧ください
                <br />
                <a
                  className={"underline"}
                  href={
                    "https://tayori.com/faq/95dd3fab44b53f9e7089a48211fbca96638ff9ef/detail/596a36414d6b757e1527157b82fe22327e9a349e/"
                  }
                  rel="noreferrer"
                  target={"_blank"}
                >
                  https://tayori.com/faq/95dd3fab44b53f9e7089a48211fbca96638ff9ef/detail/596a36414d6b757e1527157b82fe22327e9a349e/
                </a>
              </p>
            </div>
          </div>
          <div>
            <div className={"mb-8"}>
              <SectionTitle title={"よくあるご質問"} />
            </div>
            {faqs.map((faq, index) => (
              <dl
                className={`border-b border-gray-300 hover:border-blue-700 duration-300`}
                key={index}
              >
                <Disclosure as="div" key={faq.question}>
                  {({ open }) => (
                    <>
                      <dt className="text-lg">
                        <Disclosure.Button className="py-5 text-left w-full flex justify-between items-start text-gray-400">
                          <span className="text-xl text-gray-900">
                            {faq.question}
                          </span>
                          <span className="ml-6 h-7 flex items-center">
                            <ChevronDownIcon
                              className={`h-6 w-6 transform ${
                                open ? "-rotate-180" : "rotate-0"
                              }`}
                              aria-hidden="true"
                            />
                          </span>
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel as="dd" className="pr-12 pb-5">
                        <p
                          className="text-lg text-gray-500 break-words"
                          dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              </dl>
            ))}
          </div>
        </div>
        <ScrollToTop />
      </main>
    </>
  );
};

export async function getStaticProps() {
  return {
    props: {},
  };
}
export default PlansPage;
